import { quoteExcerpt } from '../huddlePortal/consoleConversationState';
import type { ConsoleQuote } from '../huddlePortal/huddleApi';
import type { SupabaseClient } from '../../lib/supabase';
import type { Json } from '../../types/database.types';
import type { RoomListItem } from './rooms';
import { PIN_FULL_HINT, PIN_LIMIT } from './pins';

export { PIN_FULL_HINT, PIN_LIMIT };

/**
 * Drop 3c data for the Messages page: threads, pins, forwards and delete.
 *
 * The database half (Dev3, 20270183000100 and 20270184000100) owns every
 * rule. Nothing here is authorization: can_post_in_room, message_pins RLS,
 * forward_message and delete_message decide. This file only asks and maps
 * the named errors to plain words.
 */
/** The row text a tombstone shows in place of its body (F2 A). */
export const TOMBSTONE_TEXT = 'This message was deleted';

/** A forward's header snapshot, written only by forward_message. */
export interface ForwardMeta {
  roomLabel: string;
  roomKind: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  /** The forwarder's note, trimmed. Absent when there was none. */
  note: string | null;
}

/** The Drop 3c columns of one message, read beside the ordinary ones. */
export interface MessageExtras {
  parentCommentId: string | null;
  forwardedFromCommentId: string | null;
  forwardedFromProjectId: string | null;
  forwardMeta: ForwardMeta | null;
  deletedAt: string | null;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * forward_meta arrives as jsonb. Anything missing reads as empty rather than
 * throwing: the header is a snapshot and a half-written one should still
 * render the copied text.
 */
export function parseForwardMeta(raw: Json | null | undefined): ForwardMeta | null {
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const note = asText(record['note']).trim();
  return {
    roomLabel: asText(record['room_label']),
    roomKind: asText(record['room_kind']),
    authorId: asText(record['author_id']),
    authorName: asText(record['author_name']),
    createdAt: asText(record['created_at']),
    note: note === '' ? null : note,
  };
}

/**
 * "Forwarded from <room>, <author>, <time>". For a DM the room is never named
 * (Dev4 A2): naming it would tell the target room who the forwarder talks to
 * in private.
 */
export function forwardHeader(meta: ForwardMeta, formatTime: (value: string) => string): string {
  const where =
    meta.roomKind === 'dm' ? 'a direct message' : meta.roomLabel === '' ? 'a room' : meta.roomLabel;
  const who = meta.authorName === '' ? 'Someone' : meta.authorName;
  const when = meta.createdAt === '' ? '' : formatTime(meta.createdAt);
  return when === ''
    ? `Forwarded from ${where}, ${who}`
    : `Forwarded from ${where}, ${who}, ${when}`;
}

export function extrasOf(row: {
  parent_comment_id?: string | null;
  forwarded_from_comment_id?: string | null;
  forwarded_from_project_id?: string | null;
  forward_meta?: Json | null;
  deleted_at?: string | null;
}): MessageExtras {
  return {
    parentCommentId: row.parent_comment_id ?? null,
    forwardedFromCommentId: row.forwarded_from_comment_id ?? null,
    forwardedFromProjectId: row.forwarded_from_project_id ?? null,
    forwardMeta: parseForwardMeta(row.forward_meta),
    deletedAt: row.deleted_at ?? null,
  };
}

/* ─── The room read: extras for every message in the open room ─── */

const IN_CHUNK = 200;

function chunks<T>(list: T[]): T[][] {
  const out: T[][] = [];
  for (let index = 0; index < list.length; index += IN_CHUNK) {
    out.push(list.slice(index, index + IN_CHUNK));
  }
  return out;
}

/**
 * The Drop 3c columns for one room's messages, keyed by comment id. A second
 * read beside fetchTaskComments, because that function is shared with every
 * Discussion tab. The one exception is parent_comment_id, which
 * fetchTaskComments also reads so a reply never flashes in the room list
 * while this read loads or after it fails (Dev4 N2).
 */
export async function fetchRoomExtras(
  client: SupabaseClient,
  roomId: string,
): Promise<Map<string, MessageExtras>> {
  const { data, error } = await client
    .from('task_comments')
    .select(
      'id, parent_comment_id, forwarded_from_comment_id, forwarded_from_project_id, forward_meta, deleted_at',
    )
    .eq('project_id', roomId)
    .is('task_id', null);
  if (error) {
    throw new Error(error.message);
  }
  const out = new Map<string, MessageExtras>();
  for (const row of (data ?? []) as unknown as {
    id: string;
    parent_comment_id: string | null;
    forwarded_from_comment_id: string | null;
    forwarded_from_project_id: string | null;
    forward_meta: Json | null;
    deleted_at: string | null;
  }[]) {
    out.set(row.id, extrasOf(row));
  }
  return out;
}

/**
 * Which forward sources this reader can still open. RLS returns no row for a
 * source in a room the reader cannot see, and a hard-deleted source has no
 * row at all; a tombstoned one comes back with deleted_at. All three read as
 * "Original not available to you" (data-model 1.2).
 */
export async function fetchVisibleSources(
  client: SupabaseClient,
  sourceIds: string[],
): Promise<Map<string, ConsoleQuote & { projectId: string }>> {
  const unique = [...new Set(sourceIds)];
  const visible = new Map<string, ConsoleQuote & { projectId: string }>();
  for (const part of chunks(unique)) {
    const { data, error } = await client
      .from('task_comments')
      .select('id, deleted_at, project_id, body, profiles:author_id (display_name)')
      .in('id', part);
    if (error) {
      throw new Error(error.message);
    }
    for (const row of (data ?? []) as unknown as {
      id: string;
      deleted_at: string | null;
      project_id: string;
      body: string;
      profiles: { display_name: string } | null;
    }[]) {
      if (row.deleted_at === null) {
        visible.set(row.id, {
          id: row.id,
          projectId: row.project_id,
          author: row.profiles?.display_name?.trim() || 'Someone',
          excerpt: quoteExcerpt(row.body),
          available: true,
        });
      }
    }
  }
  return visible;
}

/* ─── Pins ─── */

export interface RoomPin {
  commentId: string;
  pinnedBy: string;
  pinnedAt: string;
}

/** Newest pin first, as the strip lists them. */
export async function fetchRoomPins(client: SupabaseClient, roomId: string): Promise<RoomPin[]> {
  const { data, error } = await client
    .from('message_pins')
    .select('comment_id, pinned_by, pinned_at')
    .eq('project_id', roomId)
    .order('pinned_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  return ((data ?? []) as { comment_id: string; pinned_by: string; pinned_at: string }[]).map(
    (row) => ({ commentId: row.comment_id, pinnedBy: row.pinned_by, pinnedAt: row.pinned_at }),
  );
}

/** Only the two granted columns; pinned_by and pinned_at are defaults. */
export async function pinMessage(
  client: SupabaseClient,
  input: { commentId: string; roomId: string },
): Promise<void> {
  const { error } = await client
    .from('message_pins')
    .insert({ comment_id: input.commentId, project_id: input.roomId });
  if (error) {
    throw new Error(error.message);
  }
}

export async function unpinMessage(client: SupabaseClient, commentId: string): Promise<void> {
  const { error } = await client.from('message_pins').delete().eq('comment_id', commentId);
  if (error) {
    throw new Error(error.message);
  }
}

/* ─── Threads ─── */

/** A reply in a thread: an ordinary room message with parent_comment_id. */
export async function postThreadReply(
  client: SupabaseClient,
  input: {
    roomId: string;
    parentCommentId: string;
    authorId: string;
    body: string;
    mentionedUserIds: string[];
  },
): Promise<{ mentionError: string | null }> {
  const { data, error } = await client
    .from('task_comments')
    .insert({
      task_id: null,
      project_id: input.roomId,
      deal_id: null,
      social_post_id: null,
      author_id: input.authorId,
      body: input.body.trim(),
      parent_comment_id: input.parentCommentId,
    })
    .select('id')
    .single();
  if (error) {
    throw new Error(error.message);
  }
  const uniqueIds = [...new Set(input.mentionedUserIds)];
  if (uniqueIds.length > 0) {
    const { error: mentionError } = await client.from('comment_mentions').insert(
      uniqueIds.map((userId) => ({
        comment_id: data.id,
        project_id: input.roomId,
        deal_id: null,
        social_post_id: null,
        mentioned_user_id: userId,
      })),
    );
    if (mentionError) {
      return { mentionError: mentionError.message };
    }
  }
  return { mentionError: null };
}

/* ─── Forward ─── */

export async function forwardWouldWiden(
  client: SupabaseClient,
  input: { sourceRoomId: string; targetRoomId: string },
): Promise<boolean> {
  const { data, error } = await client.rpc('forward_would_widen', {
    p_source_room: input.sourceRoomId,
    p_target_room: input.targetRoomId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data === true;
}

/** Returns the new message's id. The note is trimmed by the database too. */
export async function forwardMessage(
  client: SupabaseClient,
  input: { sourceCommentId: string; targetRoomId: string; note: string },
): Promise<string> {
  const { data, error } = await client.rpc('forward_message', {
    p_source: input.sourceCommentId,
    p_target: input.targetRoomId,
    p_note: input.note.trim(),
  });
  if (error) {
    throw new Error(error.message);
  }
  return String(data);
}

/**
 * Where a message may be forwarded: every room, group and DM in the list
 * except the one it came from. The list is what the reader can see; a Viewer
 * never reaches this (the page hides Forward for read-only people), and the
 * database refuses a target the caller cannot post in
 * ('forward_target_not_allowed').
 */
export function forwardTargets(rooms: RoomListItem[], sourceRoomId: string) {
  const others = rooms.filter((room) => room.id !== sourceRoomId);
  return {
    channels: others.filter((room) => room.kind === 'channel'),
    groups: others.filter((room) => room.kind === 'group'),
    dms: others.filter((room) => room.kind === 'dm'),
  };
}

/* ─── Delete ─── */

/** 'deleted' (gone) or 'tombstoned' (it had replies, F2 A). */
export async function deleteMessage(
  client: SupabaseClient,
  commentId: string,
): Promise<'deleted' | 'tombstoned'> {
  const { data, error } = await client.rpc('delete_message', { p_comment: commentId });
  if (error) {
    throw new Error(error.message);
  }
  return data === 'tombstoned' ? 'tombstoned' : 'deleted';
}

/* ─── Named errors, in plain words ─── */

const DROP3C_ERROR_MESSAGES: Record<string, string> = {
  room_pins_full: `This room already has ${String(PIN_LIMIT)} pins. ${PIN_FULL_HINT}.`,
  pin_not_allowed_here: 'Only room messages can be pinned, not thread replies.',
  forward_too_long:
    'This message is too long to forward with that note. Shorten the note and try again.',
  forward_source_not_found: 'That message is no longer available to forward.',
  forward_source_deleted: 'That message was deleted, so it cannot be forwarded.',
  forward_target_not_allowed: 'You cannot post in that chat, so it cannot be forwarded there.',
  thread_parent_not_found: 'That message is no longer available, so the reply was not sent.',
  thread_parent_deleted: 'That message was deleted, so the reply was not sent.',
  comment_not_found: 'That message is no longer there.',
  delete_not_allowed: 'Only the person who wrote a message can delete it.',
  comment_is_deleted: 'That message was deleted, so it cannot be edited.',
  comment_edit_not_permitted: 'Only the person who wrote a message can edit it.',
};

/**
 * The database raises these with the name as the whole message (P0001). A
 * PostgREST error sometimes wraps it, so a name anywhere in the text counts.
 */
export function drop3cErrorMessage(raw: string, fallback: string): string {
  const exact = DROP3C_ERROR_MESSAGES[raw];
  if (exact !== undefined) {
    return exact;
  }
  const named = Object.keys(DROP3C_ERROR_MESSAGES).find((key) => raw.includes(key));
  return named === undefined ? fallback : (DROP3C_ERROR_MESSAGES[named] ?? fallback);
}

/* ─── Splitting one room read into top level and threads ─── */

export interface ThreadSummary {
  count: number;
  lastAt: string;
  /** Distinct repliers, most recent first, at most three. */
  faces: { authorId: string; name: string; avatarUrl: string | null }[];
}

/**
 * The page reads the whole room once (fetchTaskComments, no limit), so the
 * "N replies, last reply" line is counted here from data it already has
 * (data-model 3). A reply whose parent is not in the read is dropped from
 * the room list rather than shown as a stray top-level line.
 *
 * The parent comes from the message row itself first (fetchTaskComments
 * reads parent_comment_id), and from the extras map only as a fallback. So a
 * reply stays out of the room list while the extras read is still loading,
 * if it failed, or if a poll brought the reply in before extras caught up
 * (Dev4 N2). Top-level messages show as before in all three cases.
 */
export function splitThreads<
  T extends {
    id: string;
    created_at: string;
    author_id: string;
    author_name: string;
    parent_comment_id?: string | null;
  },
>(
  messages: T[],
  extras: Map<string, MessageExtras> | undefined,
  avatarOf: (message: T) => string | null,
): { topLevel: T[]; replies: Map<string, T[]>; summaries: Map<string, ThreadSummary> } {
  const replies = new Map<string, T[]>();
  const topLevel: T[] = [];
  for (const message of messages) {
    const parent = message.parent_comment_id ?? extras?.get(message.id)?.parentCommentId ?? null;
    if (parent === null) {
      topLevel.push(message);
    } else {
      const list = replies.get(parent) ?? [];
      list.push(message);
      replies.set(parent, list);
    }
  }
  const summaries = new Map<string, ThreadSummary>();
  for (const [parentId, list] of replies) {
    list.sort((a, b) => a.created_at.localeCompare(b.created_at));
    const faces: ThreadSummary['faces'] = [];
    for (const reply of [...list].reverse()) {
      if (faces.length >= 3) {
        break;
      }
      if (!faces.some((face) => face.authorId === reply.author_id)) {
        faces.push({
          authorId: reply.author_id,
          name: reply.author_name === '' ? 'Someone' : reply.author_name,
          avatarUrl: avatarOf(reply),
        });
      }
    }
    summaries.set(parentId, {
      count: list.length,
      lastAt: list[list.length - 1]?.created_at ?? '',
      faces,
    });
  }
  return { topLevel, replies, summaries };
}

/**
 * A tombstone with no replies left is an empty shell (Dev4 A3). The database
 * removes it when the last reply goes; the page also hides one, so a read
 * between the two never shows "This message was deleted" with nothing under it.
 */
export function isEmptyShell(
  messageId: string,
  extras: Map<string, MessageExtras> | undefined,
  replies: Map<string, unknown[]>,
): boolean {
  const deletedAt = extras?.get(messageId)?.deletedAt ?? null;
  return deletedAt !== null && (replies.get(messageId)?.length ?? 0) === 0;
}
